import {
  Component,
  Output,
  EventEmitter,
  Input,
  OnChanges,
} from '@angular/core';
import { Constants } from 'src/app/models/constants';
import { QRButton } from 'src/app/models/qr-button.model';
import { QRDropdown } from 'src/app/models/qr-dropdown.model';
import { QRInput } from 'src/app/models/qr-input.model';
import {
  DecimalAmountField,
  DecimalAmountInput,
} from '@base/src/app/utils/decimal-amount.utils';
import {
  Data,
  ObjectiveAnnualUpdate,
  ObjectiveException,
  ObjectivesData,
} from '@base/src/app/pages/configuration/panel/configuration.component';

/** Keys of `ObjectivesData` that are not objective groups. */
const NON_GROUP_KEYS = ['agents', 'canEdit', 'message'];

@Component({
  selector: 'app-objectives',
  templateUrl: './objectives.component.html',
  styleUrls: ['./objectives.component.scss'],
})
export class ObjectivesComponent implements OnChanges {
  @Input() objectivesYears: string[] = [];
  @Input() objectives!: ObjectivesData;
  @Output() downloadExcel: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() changePermissions: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() addException: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() changeYear: EventEmitter<string> = new EventEmitter<string>();
  @Output() removeException: EventEmitter<ObjectiveException> =
    new EventEmitter<ObjectiveException>();
  @Output() openMonthlyDetailed: EventEmitter<Data> =
    new EventEmitter<Data>();
  @Output() updateObjective: EventEmitter<ObjectiveAnnualUpdate> =
    new EventEmitter<ObjectiveAnnualUpdate>();

  formSelected = 'office';

  dropdownYears: QRDropdown = {
    hierarchy: QRDropdown.HIERARCHY_1,
    size: QRDropdown.SIZE_MEDIUM,
    placeholder: 'Seleccionar',
    options: this.objectivesYears,
  };

  input: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: '0',
    debounceTime: 0,
  };

  buttonCalendar: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Editar detalle mensual',
    icon: Constants.ICONS.CALENDAR_VIEW,
  };

  buttonCalendarNoEdit: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Ver detalle mensual',
    icon: Constants.ICONS.CALENDAR_VIEW,
  };

  buttonSave: QRButton = {
    hierarchy: QRButton.HIERARCHY_1,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Guardar',
    icon: Constants.ICONS.SAVE,
  };

  buttonDownloadExcel: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Descargar objetivos',
    icon: Constants.ICONS.DOWNLOAD,
  };

  buttonException: QRButton = {
    hierarchy: QRButton.HIERARCHY_2,
    size: QRButton.SIZE_MEDIUM,
    viewValue: 'Añadir excepción',
    icon: Constants.ICONS.DOWNLOAD,
  };

  buttonClose: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    icon: Constants.ICONS.CLOSE,
    iconOnly: true,
  };

  translateArray: Record<string, string> = {};

  yearSelected = '';
  loading = false;

  /** Objective groups (incomes, properties, rrhh) in the order the API sends them. */
  objectiveGroups: Data[][] = [];

  /** Annual field per row; kept off `row` so it is not sent in the payload. */
  private annualFields = new Map<Data, DecimalAmountField>();

  ngOnChanges(): void {
    this.loading = true;
    this.translateArray['incomes'] = 'Ingresos';
    this.translateArray['rrhh'] = 'Red (RRHH)';
    this.translateArray['properties'] = 'Propiedades';
    this.objectives = { ...this.objectives };
    this.buildAnnualFields();
    this.objectivesYears.sort((a, b) => (a < b ? 1 : -1));
    this.dropdownYears.options = this.objectivesYears;
    this.yearSelected = this.objectivesYears[0];
    this.loading = false;
  }

  dropdownYearsChange(event: string) {
    if (event) this.changeYear.emit(event);
  }

  getAnnualField(row: Data): DecimalAmountField {
    let field = this.annualFields.get(row);
    if (!field) {
      field = new DecimalAmountField(row?.anual);
      this.annualFields.set(row, field);
    }
    return field;
  }

  onAnnualInput(event: DecimalAmountInput, row: Data) {
    if (!row) return;
    this.getAnnualField(row).onInput(event);
  }

  commitAnnualValue(row: Data) {
    if (!row) return;

    const FIELD = this.getAnnualField(row);
    if (!FIELD.commit()) return;

    row.anual = FIELD.value;
    this.updateObjective.emit({
      value: FIELD.value,
      type: row.type,
    });
  }

  private buildAnnualFields() {
    this.annualFields = new Map<Data, DecimalAmountField>();

    this.objectiveGroups = Object.entries(this.objectives ?? {})
      .filter(([key]) => !NON_GROUP_KEYS.includes(key))
      .map(([, group]) => group)
      .filter((group): group is Data[] => Array.isArray(group));

    this.objectiveGroups.forEach((group) => {
      group.forEach((row) => {
        if (row && typeof row === 'object' && 'anual' in row) {
          this.annualFields.set(row, new DecimalAmountField(row.anual));
        }
      });
    });
  }
}
